import { downloadPdfBlob } from '../../shared/helpers';
import { axiosInstance } from './apiAxiosInstance';

export interface Part {
  id: number;
  name: string;
  sanitizedName: string;
  sequenceNumber: number;
  mark: string;
  globalId: string;
  subType: string;
  type: string;
  weight: number | null;
  qty: number;
  metadata?: any;
  stlFileStorageId: string | null;
  isIncluded: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PartResponse {
  message: string;
  loadingPlanId: number;
  partsCreated: number;
  parts: Part[];
}

export interface CreatePartRequest {
  loadingPlanId: number;
  mark: string;
  lotSeq?: string;
  type?: string;
  shape?: string;
  quantity: number;
  length: number;
  width: number;
  height: number;
  weight: number;
  description?: string;
}

export interface FetchPartsParams {
  loadingPlanId: number;
  limit?: number;
  offset?: number;
  type?: string[];
  mark?: string[];
  // Wildcard mark mask ('*', '?'), matched on the server.
  markMask?: string;
  sequenceFrom?: number;
  sequenceTo?: number;
  isIncluded?: boolean;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

export interface FetchPartsResponse {
  limit: number;
  offset: number;
  parts: Part[];
  total: number;
}

export interface SignedLinkPart {
  partId: number;
  name: string;
  mark: string;
  sasUrl: string;
}

export interface SignedLinkSupportModel {
  sasUrl: string;
  name: string;
}

export interface SignedLinksResponse {
  parts: SignedLinkPart[];
  supportModels: SignedLinkSupportModel[];
}

export interface WeightsResponse {
  total: number;
  loaded: number;
}

const partsService = {
  /**
   * Upload IFC file for a loading plan
   */
  async uploadIfcFile(loadingPlanId: number, file: File): Promise<PartResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('loadingPlanId', loadingPlanId.toString());

    const response = await axiosInstance.post<PartResponse>(
      '/api/v1/parts/upload-ifc',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  /**
   * Fetch parts with pagination and filters
   */
  async fetchParts(params: FetchPartsParams): Promise<FetchPartsResponse> {
    const response = await axiosInstance.get<FetchPartsResponse>('/api/v1/parts', {
      params,
      paramsSerializer: {
        indexes: null, // This will generate type=A&type=AB format
      },
    });
    return response.data;
  },

    /**
   * Fetch parts for loading plan view
   */
  async fetchLoadingPlanViewParts(loadingPlanId: number): Promise<FetchPartsResponse> {
    const response = await axiosInstance.get<FetchPartsResponse>('/api/v1/parts/loading-plan-view', {
      params: { loadingPlanId },
    });
    return response.data;
  },

  /**
   * Fetch available part types
   */
  async fetchPartTypes(): Promise<string[]> {
    const response = await axiosInstance.get<string[]>('/api/v1/parts/types');
    return response.data;
  },

  /**
   * Fetch available part marks
   */
  async fetchPartMarks(): Promise<string[]> {
    const response = await axiosInstance.get<string[]>('/api/v1/parts/marks');
    return response.data;
  },

  /**
   * Fetch available filters (types, marks, and sequence numbers)
   */
  async fetchFilters(loadingPlanId: number): Promise<{
    types: string[];
    marks: string[];
    minSequence?: number;
    maxSequence?: number;
  }> {
    const response = await axiosInstance.get<{
      types: string[];
      marks: string[];
      minSequence?: number;
      maxSequence?: number;
    }>('/api/v1/parts/filters', {
      params: { loadingPlanId },
    });
    return response.data;
  },

  /**
   * Fetch STL file for a part
   */
  async fetchPartStl(loadingPlanId: number, filename: string): Promise<Blob> {
    const response = await axiosInstance.get('/api/v1/parts/stl', {
      params: {
        loadingPlanId,
        filename,
      },
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Fetch signed SAS URLs for parts
   */
  async fetchSignedLinks(loadingPlanId: number, expiresInMinutes: number = 60): Promise<SignedLinksResponse> {
    const response = await axiosInstance.get('/api/v1/parts/signed-links', {
      params: {
        loadingPlanId,
        expiresInMinutes,
      },
    });
    return response.data;
  },

  /**
   * Create new parts (e.g. from hand inclusion)
   */
  async addIncludeParts(data: { loadingPlanId: string, globalIds: string[] }): Promise<PartResponse> {
    const response = await axiosInstance.post<any>(
      '/api/v1/parts/update-inclusion',
      data
    );
    return response.data;
  },

  /**
   * Fetch total and loaded weights for a loading plan
   */
  async fetchWeights(loadingPlanId: number): Promise<WeightsResponse> {
    const response = await axiosInstance.get<WeightsResponse>('/api/v1/parts/weights', {
      params: { loadingPlanId },
    });
    return response.data;
  },

    /**
   * Export PDF with included parts for a loading plan
   */
  async exportPdf(loadingPlanId: number): Promise<void> {
    const response = await axiosInstance.get(`/api/v1/parts/export-pdf`, {
      params: { loadingPlanId },
      responseType: 'blob',
    });
    
    const blob = new Blob([response.data], { type: 'application/pdf' });
    downloadPdfBlob(blob, `loading-plan-${loadingPlanId}.pdf`);
  },
};

export default partsService;
